import { ReactNode } from 'react';

import {
  Autocomplete,
  DropdownProps,
  Select,
  SelectProps,
  SxProps,
} from '@cast/design-system';

import { useSearchCriterion } from './hooks';
import { Criterion } from './types';

export type FilterOption<T extends string = string> = {
  title: string;
  value: T;
};

type FilterSelectProps<
  T extends string = string,
  Multiple extends boolean | undefined = undefined
> = {
  criterion?: Criterion | string;
  startAdornment?: ReactNode;
  options: Array<FilterOption<T> | T>;
  sx?: SxProps;
  inputSx?: SxProps;
  renderOption?: DropdownProps<
    FilterOption<Multiple extends true ? T : T | 'All'>,
    T,
    Multiple extends true ? 'multiple' : 'single'
  >['renderOption'];
  analyticsHandler?: (value: T | T[]) => void;
  testId?: string;
  SelectProps?: Partial<
    SelectProps<
      FilterOption<Multiple extends true ? T : T | 'All'>,
      T,
      Multiple extends true ? 'multiple' : 'single'
    >
  >;
  multiple?: Multiple;
  autocomplete?: boolean;
};

export const FilterSelect = <
  T extends string,
  Multiple extends boolean | undefined
>({
  criterion: _criterion,
  options,
  startAdornment,
  sx,
  inputSx,
  renderOption,
  analyticsHandler,
  testId,
  SelectProps,
  multiple,
  autocomplete,
}: FilterSelectProps<T, Multiple>) => {
  const Dropdown = autocomplete ? Autocomplete : Select;
  const criterion = useSearchCriterion(_criterion);
  const isMultiple = (SelectProps?.multiple as Multiple) ?? multiple;
  const normalizedOptions = options.map((opt) =>
    typeof opt === 'string' ? { title: opt, value: opt } : opt
  );

  const opts: FilterOption<T>[] = isMultiple
    ? normalizedOptions
    : [{ title: 'All', value: 'All' } as FilterOption<T>, ...normalizedOptions];

  const val = (() => {
    if (isMultiple) {
      if (!criterion?.value?.includes) {
        return [];
      }
      return opts.filter(({ value }) => criterion.value.includes(value));
    }
    return (
      (criterion?.value && opts.find((opt) => opt.value === criterion.value)) ||
      opts[0]
    );
  })();

  return (
    <Dropdown<FilterOption<T>, T>
      sx={sx}
      inputProps={{ sx: inputSx }}
      value={val}
      onChange={(val: FilterOption<T> | FilterOption<T>[]) => {
        const value = Array.isArray(val)
          ? val.map(({ value }) => value)
          : val.value;
        if (Array.isArray(value)) {
          criterion.setValue?.(value);
          analyticsHandler?.(value);
        } else {
          if (value === 'All') {
            criterion?.clear?.();
          } else {
            criterion?.setValue?.(value);
          }
          analyticsHandler?.(value);
        }
      }}
      isOptionEqualToValue={(opt, val: any) => {
        return opt.value === val.value;
      }}
      startAdornment={startAdornment}
      optionLabel="title"
      options={opts}
      renderOption={renderOption}
      testId={testId}
      {...(SelectProps as any)}
      multiple={isMultiple}
      checkable={isMultiple}
    />
  );
};
